import React, { useState, useEffect } from "react";
import Editor from "@monaco-editor/react";
import axios from "axios";
import Joyride from "react-joyride";
import Select from "react-select";
import SEO from "../components/SEO";

const JavaSnippetPage = () => {
  const [javaSnippets, setJavaSnippets] = useState([]);
  const [selectedSnippet, setSelectedSnippet] = useState(null);
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [isExplanationOpen, setIsExplanationOpen] = useState(false);
  const [runTour, setRunTour] = useState(false);
  const [tourSteps, setTourSteps] = useState([]);
  const [editorHeight, setEditorHeight] = useState(window.innerWidth < 640 ? 250 : 300);

  // Fetch Java snippets from backend
  useEffect(() => {
    const API_KEY = process.env.REACT_APP_API_KEY;
    const JAVA_API = process.env.REACT_APP_JAVA_SNIPPET_API_KEY;

    const fetchSnippets = async () => {
      try {
        const response = await axios.get(JAVA_API, {
          headers: { "X-API-KEY": API_KEY, "Content-Type": "application/json" },
        });
        setJavaSnippets(response.data);
        setSelectedSnippet(response.data[0]);
      } catch (error) {
        console.error("Failed to fetch Java snippets:", error);
      }
    };
    fetchSnippets();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Adjust editor height on window resize
    const handleResize = () => {
      setEditorHeight(window.innerWidth < 640 ? 250 : 300);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Joyride setup
  useEffect(() => {
    const hasSeenTour = localStorage.getItem("seenTour");
    if (!hasSeenTour) {
      const checkElementsReady = setInterval(() => {
        const editor = document.querySelector("#editor-container");
        const runBtn = document.querySelector("#run-button");
        const explainBtn = document.querySelector("#explanation-button");
        if (editor && runBtn && explainBtn) {
          clearInterval(checkElementsReady);
          setTourSteps([
            {
              target: "body",
              placement: "center",
              content: "Welcome to the Java Online Compiler! Let's take a quick tour.",
              locale: { skip: "Skip Tour" },
            },
            {
              target: "#mobile-snippet",
              content: "Select a code snippet from the list or search here.",
            },
            {
              target: "#editor-container",
              content: "This is the Java editor where you can write or edit Java code.",
            },
            {
              target: "#run-button",
              content: "Click here to run your Java code and view the output below.",
            },
            {
              target: "#explanation-button",
              content: "Click this to view the explanation for the snippet.",
            },
          ]);
          setRunTour(true);
          localStorage.setItem("seenTour", "true");
        }
      }, 500);
      return () => clearInterval(checkElementsReady);
    }
  }, []);

  const runCode = async () => {
    setLoading(true);
    setOutput("Running...");
    try {
      const response = await axios.post("https://emkc.org/api/v2/piston/execute", {
        language: "java",
        version: "15.0.2",
        files: [{ name: "Main.java", content: selectedSnippet.code }],
      });
      setOutput(response.data?.run?.output || "No output");
    } catch (err) {
      setOutput("Error running code:\n" + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row min-h-screen dark:bg-gray-900 dark:text-white relative">
      {/* SEO */}
      <SEO
        title="Java Online Compiler : Run Java Code Instantly"
        description="Run Java code online with our Java Online Compiler, explore code snippets, learn programming, and practice Java easily."
        keywords="java code, java online compiler, programming, coderocker, run java online, free java compiler, java editor"
        url="https://coderocker.netlify.app/java-snippet"
      />

      {/* Joyride */}
      <Joyride
        steps={tourSteps}
        continuous
        showProgress
        showSkipButton
        run={runTour}
        disableScrolling={true}
        spotlightClicks
        styles={{ options: { zIndex: 10000, primaryColor: "#4F46E5" } }}
        callback={(data) => {
          if (data.status === "finished" || data.status === "skipped") setRunTour(false);
        }}
      />

      {/* H1 for SEO */}
      <div className="w-full px-4 py-4 lg:hidden text-center">
        <h1 className="text-2xl sm:text-3xl font-bold text-indigo-600 dark:text-indigo-400">
          Java Online Compiler : Run Java Code Instantly
        </h1>
      </div>

      {/* Mobile Dropdown */}
      <div className="lg:hidden w-full mb-4 px-4">
        <Select
          id="mobile-snippet"
          placeholder="Select from list of snippets..."
          options={javaSnippets.map(snippet => ({
            value: snippet.title,
            label: snippet.title
          }))}
          value={selectedSnippet ? { value: selectedSnippet.title, label: selectedSnippet.title } : null}
          onChange={(option) => {
            const snippet = javaSnippets.find(s => s.title === option.value);
            setSelectedSnippet(snippet);
            setIsExplanationOpen(false);
          }}
          isSearchable
          styles={{
            control: (provided, state) => ({
              ...provided,
              backgroundColor: state.isFocused ? "#f0f0f0" : "#ffffff",
              borderColor: "#ccc",
              minHeight: "40px",
              fontSize: "14px",
            }),
            menu: (provided) => ({ ...provided, backgroundColor: "#fff", zIndex: 9999 }),
            option: (provided, state) => ({
              ...provided,
              backgroundColor: state.isFocused ? "#e0e0e0" : "#fff",
              color: "#000",
            }),
            singleValue: (provided) => ({ ...provided, color: "#000" }),
            placeholder: (provided) => ({ ...provided, color: "#888" }),
          }}
          theme={(theme) => ({
            ...theme,
            colors: { ...theme.colors, primary25: '#e0e0e0', primary: '#4F46E5' },
          })}
        />
      </div>

      {/* Desktop Left Panel */}
      <div className="hidden lg:block lg:w-1/4 border-r border-gray-300 dark:border-gray-700 p-4">
        <h2 className="text-xl font-bold mb-4 text-indigo-600 dark:text-indigo-400">Java Snippets</h2>
        <ul className="space-y-2 max-h-[80vh] overflow-y-auto">
          {javaSnippets.map((snippet, idx) => (
            <li
              key={idx}
              onClick={() => { setSelectedSnippet(snippet); setIsExplanationOpen(false); }}
              className={`cursor-pointer p-2 rounded transition ${
                selectedSnippet?.title === snippet.title
                  ? "bg-indigo-500 text-white"
                  : "hover:bg-gray-200 dark:hover:bg-gray-800"
              }`}
            >
              {snippet.title}
            </li>
          ))}
        </ul>
      </div>

      {/* Right Panel */}
      <div className="flex-1 p-4 flex flex-col overflow-x-hidden">
        {/* Desktop H1 */}
        <h1 className="hidden lg:block text-3xl font-bold text-indigo-600 dark:text-indigo-400 mb-4">
          Java Online Compiler: Run Java Code Instantly
        </h1>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2 gap-2">
          <h2 className="text-xl sm:text-2xl font-bold">{selectedSnippet?.title}</h2>
          <button
            id="explanation-button"
            className="flex items-center gap-2 text-sm px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded"
            onClick={() => setIsExplanationOpen(true)}
          >
            Explanation
          </button>
        </div>

        {/* Editor */}
        <div id="editor-container" className="w-full">
          <Editor
            height={editorHeight}
            defaultLanguage="java"
            value={selectedSnippet?.code || ""}
            onChange={(newValue) => setSelectedSnippet(prev => ({ ...prev, code: newValue }))}
            theme="vs-dark"
            options={{
              fontSize: window.innerWidth < 640 ? 12 : 14,
              minimap: { enabled: false },
              automaticLayout: true,
              wordWrap: "on",
              padding: { top: 8, bottom: 8 },
            }}
            onFocus={() => window.innerWidth < 640 && setEditorHeight(350)}
            onBlur={() => window.innerWidth < 640 && setEditorHeight(250)}
          />
        </div>

        {/* Run Button */}
        <button
          id="run-button"
          onClick={runCode}
          disabled={loading}
          className="mt-4 px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded w-full sm:w-40"
        >
          {loading ? "Running..." : "Run Code"}
        </button>

        {/* Output */}
        <div className="mt-6">
          <h2 className="text-lg font-semibold">Output:</h2>
          <pre className="bg-black text-green-400 p-4 mt-2 rounded whitespace-pre-wrap overflow-x-auto">
            {output}
          </pre>
        </div>
      </div>

      {/* Explanation Panel */}
      {isExplanationOpen && selectedSnippet && (
        <div
          className={`fixed lg:absolute ${
            window.innerWidth < 1024
              ? "bottom-0 w-full h-1/2"
              : "right-0 top-0 w-1/3 h-full"
          } bg-white dark:bg-gray-800 shadow-lg z-50 transition-transform duration-300`}
        >
          <div className="p-6 flex flex-col h-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Explanation</h2>
              <button className="text-red-600 hover:text-red-800" onClick={() => setIsExplanationOpen(false)}>
                &times;
              </button>
            </div>
            <p className="text-gray-700 dark:text-gray-300 overflow-y-auto">{selectedSnippet.explanation}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default JavaSnippetPage;
